import React, { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";

const PublicAlbumView = ({ isAuthenticated, user }) => {
  const { shareId } = useParams();
  const location = useLocation();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [alreadyTagged, setAlreadyTagged] = useState(false);
  const [taggingLoad, setTaggingLoad] = useState(false);

  useEffect(() => {
    fetchPublicAlbum();
  }, [shareId]);

  // Check if user is already tagged when they become authenticated
  useEffect(() => {
    if (isAuthenticated && user && album) {
      checkIfUserTagged();
    }
  }, [isAuthenticated, user, album]);

  const fetchPublicAlbum = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/public/${shareId}`
      );
      setAlbum(response.data);
      setCurrentPhotoIndex(0);
    } catch (err) {
      console.error("Error fetching public album:", err);
      setError(
        err.response?.data?.message ||
          "This album doesn't exist or isn't public"
      );
    } finally {
      setLoading(false);
    }
  };

  const checkIfUserTagged = async () => {
    if (!user || !album || alreadyTagged) return;

    try {
      // Check if the user is tagged in any photos in this album
      const response = await axios.get(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/tagged`
      );
      const userTaggedPhotos = response.data;

      // Check if any of the tagged photos belong to this album
      const isTaggedInAlbum = userTaggedPhotos.some(
        (group) =>
          group.photos &&
          group.photos.some((photo) =>
            album.photos.some((albumPhoto) => albumPhoto.id === photo.id)
          )
      );

      setAlreadyTagged(isTaggedInAlbum);
    } catch (error) {
      console.error("Error checking if user is tagged:", error);
    }
  };

  const handleTagMeInAlbum = async () => {
    if (!isAuthenticated || !user) {
      setShowAuthModal(true);
      return;
    }

    setTaggingLoad(true);
    try {
      // Send request to tag the user in all photos in this album
      const response = await axios.post(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/public/${shareId}/tag-user`,
        { userId: user._id }
      );

      if (response.data.success) {
        setAlreadyTagged(true);
        // Optionally reload the tagged photos
        checkIfUserTagged();
      }
    } catch (error) {
      console.error("Error tagging user in album:", error);
      setError("Failed to tag you in this album. Please try again.");
    } finally {
      setTaggingLoad(false);
    }
  };

  const handleDownload = async (photoId) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/${photoId}/download`,
        {
          responseType: "blob",
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `photo-${photoId}.jpg`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading photo:", error);
      if (error.response?.status === 403) {
        setShowAuthModal(true);
      }
    }
  };

  const handlePrevPhoto = () => {
    if (!album || !album.photos || album.photos.length <= 1) return;

    setCurrentPhotoIndex((prevIndex) => {
      if (prevIndex === 0) {
        return album.photos.length - 1;
      }
      return prevIndex - 1;
    });
  };

  const handleNextPhoto = () => {
    if (!album || !album.photos || album.photos.length <= 1) return;

    setCurrentPhotoIndex((prevIndex) => {
      if (prevIndex === album.photos.length - 1) {
        return 0;
      }
      return prevIndex + 1;
    });
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="mb-4">
            <svg
              className="animate-spin h-12 w-12 text-purple-600 mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <p className="text-gray-600 text-lg">Loading album...</p>
        </div>
      </div>
    );
  }

  if (error || !album) {
    return (
      <div className="max-w-4xl mx-auto p-6 pt-10 text-center">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-red-500 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Album Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            {error || "This album doesn't exist or isn't public."}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700"
            >
              Go to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const currentPhoto = album.photos[currentPhotoIndex];

  return (
    <div className="max-w-6xl mx-auto p-4 pt-6">
      {album && (
        <Helmet>
          <title>{album.albumName || "Unnamed Album"} | PhotoBomb</title>
          <meta
            property="og:title"
            content={`${album.albumName || "Unnamed Album"} | PhotoBomb`}
          />
          <meta
            property="og:description"
            content={`Album by ${album.uploader?.name || "User"} ${
              album.location ? `- ${album.location}` : ""
            }`}
          />
          {album.photos && album.photos.length > 0 && (
            <meta property="og:image" content={album.photos[0].url} />
          )}
          <meta property="og:url" content={window.location.href} />
          <meta property="og:type" content="website" />
        </Helmet>
      )}

      {/* Album header */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {album.albumName || "Unnamed Album"}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-gray-600">
              {album.location && (
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>{album.location}</span>
                </div>
              )}
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>
                  {album.photos.length}{" "}
                  {album.photos.length === 1 ? "photo" : "photos"}
                </span>
              </div>
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>
                  {new Date(album.date).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mr-3">
                {album.uploader?.profilePhotoUrl ? (
                  <img
                    src={album.uploader.profilePhotoUrl}
                    alt={album.uploader.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-medium text-purple-700">
                    {album.uploader?.name?.charAt(0)?.toUpperCase() || "?"}
                  </span>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {album.uploader?.name || "User"}
                </p>
                {album.uploader?.username && (
                  <p className="text-xs text-gray-500">
                    @{album.uploader.username}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tag me in album button for authenticated users */}
        {isAuthenticated && user && !alreadyTagged && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-blue-600 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Tag yourself in this album
                  </p>
                  <p className="text-xs text-blue-600">
                    Get tagged in all photos so you can download them
                  </p>
                </div>
              </div>
              <button
                onClick={handleTagMeInAlbum}
                disabled={taggingLoad}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {taggingLoad ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Tagging...
                  </div>
                ) : (
                  "Tag Me"
                )}
              </button>
            </div>
          </div>
        )}

        {alreadyTagged && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-green-600 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <p className="text-sm text-green-800">
                You're tagged in this album! You can download these photos.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Main photo display section */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
        <div className="relative">
          <img
            src={currentPhoto.url}
            alt={`Photo ${currentPhotoIndex + 1}`}
            className="w-full h-[70vh] object-contain bg-black"
          />

          {/* Photo counter */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center">
            <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
              {currentPhotoIndex + 1} / {album.photos.length}
            </div>
          </div>

          {/* Navigation arrows */}
          {album.photos.length > 1 && (
            <>
              <button
                onClick={handlePrevPhoto}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-3 transition-all"
                aria-label="Previous photo"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={handleNextPhoto}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-3 transition-all"
                aria-label="Next photo"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Photo thumbnail navigation */}
        {album.photos.length > 1 && (
          <div className="p-4 border-t border-gray-100">
            <div className="flex overflow-x-auto space-x-2 py-2">
              {album.photos.map((photo, photoIndex) => (
                <div
                  key={photo.id}
                  onClick={() => setCurrentPhotoIndex(photoIndex)}
                  className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden cursor-pointer border-2 ${
                    photoIndex === currentPhotoIndex
                      ? "border-purple-500"
                      : "border-transparent hover:border-purple-200"
                  }`}
                >
                  <img
                    src={photo.url}
                    alt={`Thumbnail ${photoIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Download button section */}
        <div className="p-4 border-t border-gray-100 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {album.photos.length} photos by {album.uploader?.name || "User"}
          </div>
          <button
            onClick={() => handleDownload(currentPhoto.id)}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm font-medium"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Download Photo
          </button>
        </div>
      </div>

      {/* Auth modal for non-logged in users */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="text-center mb-4">
              <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                Sign Up to Access Photos
              </h3>
              <p className="text-gray-600 mt-2">
                Create a free account to download these photos and get tagged in
                albums!
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Link
                to={`/signup?redirect=${encodeURIComponent(location.pathname)}`}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg text-center hover:from-purple-700 hover:to-pink-700"
              >
                Sign Up
              </Link>
              <Link
                to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-800 font-medium rounded-lg text-center hover:bg-gray-200"
              >
                Log In
              </Link>
            </div>
            <button
              onClick={closeAuthModal}
              className="w-full mt-4 text-gray-500 hover:text-gray-700 text-sm"
            >
              Continue Viewing
            </button>
          </div>
        </div>
      )}

      {/* Join call to action - Only show for non-authenticated users */}
      {!isAuthenticated && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl overflow-hidden shadow-lg mb-8">
          <div className="p-6 sm:p-8">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-white mb-3">
                Create Your Own Photo Albums
              </h2>
              <p className="text-purple-100 mb-6">
                Join SnapTag to create your own albums, tag friends, and
                download photos
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link
                  to={`/signup?redirect=${encodeURIComponent(
                    location.pathname
                  )}`}
                  className="px-6 py-3 bg-white text-purple-700 font-medium rounded-lg hover:bg-purple-50"
                >
                  Sign Up Free
                </Link>
                <Link
                  to={`/login?redirect=${encodeURIComponent(
                    location.pathname
                  )}`}
                  className="px-6 py-3 bg-purple-700 bg-opacity-30 text-white font-medium rounded-lg hover:bg-opacity-40"
                >
                  Log In
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicAlbumView;
